/**
 * Per-diamond tidal-stream model, from the RYA Training Charts.
 *
 * The data in `diamonds.json` is transcribed off the printed tidal-stream
 * diamond tables on RYA Training Charts 3 and 4 (a fictitious training area),
 * all referred to HW Victoria: for each lettered diamond, the set (degrees true)
 * and the rate at spring and neap tides, for each hour from −6 to +6 relative to
 * HW. Diamonds are keyed by chart+letter (e.g. `c3-A` vs `c4-A`) because the
 * letters repeat across the two charts; the lat/long position is the real
 * anchor. This is real training data, not a synthesised field, but it is NOT for
 * real-world navigation.
 *
 * Everything here is pure so the interpolation is easy to unit-test.
 */
import data from './diamonds.json';
import { tidalStateAt } from './victoria';

/** One hourly row of a diamond: set (deg true), spring rate, neap rate (kn). */
export interface DiamondHour {
  /** Hours relative to HW Victoria, −6…+6 (0 = HW). */
  h: number;
  /** Set, degrees true. */
  dir: number;
  /** Rate at spring tides, knots. */
  sp: number;
  /** Rate at neap tides, knots. */
  np: number;
}

export interface TidalDiamond {
  /** Stable id, chart+letter, e.g. `c4-A`. */
  id: string;
  /** Which chart it's printed on: `c3` or `c4`. */
  chart: string;
  /** The diamond letter as charted (A, B, …; I and O are skipped). */
  letter: string;
  /** Charted position [lon, lat]. The real anchor (letters repeat per chart). */
  position: [number, number];
  /** The HW reference port the table is referred to (Victoria). */
  refPort: string;
  /** 13 hourly rows, −6…+6 relative to HW. */
  hours: DiamondHour[];
}

interface DiamondDoc {
  refPort: string;
  note: string;
  diamonds: TidalDiamond[];
}

const doc = data as DiamondDoc;

/** All transcribed diamonds across both charts. */
export const TIDAL_DIAMONDS: TidalDiamond[] = doc.diamonds;
/** The single HW reference port for every diamond (Victoria). */
export const TIDAL_REF_PORT: string = doc.refPort;

export interface StreamSample {
  /** Set, degrees true. */
  setDeg: number;
  /** Rate, knots. */
  rateKn: number;
}

/** Mean semidiurnal tidal period, hours (used to wrap a clock onto −6…+6). */
export const TIDAL_PERIOD_H = 12.42;

/**
 * Interpolate an angle in degrees the short way around the circle, so a turn
 * from 357° to 001° passes through 000°, not all the way back through 180°.
 */
export function lerpAngleDeg(a: number, b: number, t: number): number {
  const delta = ((b - a + 540) % 360) - 180;
  return (((a + delta * t) % 360) + 360) % 360;
}

/**
 * Wrap a raw "hours since HW" value onto the diamond window −6…+6 by folding it
 * over the semidiurnal period, so a clock that's run past +6 comes back round.
 */
export function wrapHoursFromHW(rawHours: number): number {
  const half = TIDAL_PERIOD_H / 2;
  return ((((rawHours + half) % TIDAL_PERIOD_H) + TIDAL_PERIOD_H) % TIDAL_PERIOD_H) - half;
}

/**
 * Set and rate at a diamond for a given time and spring/neap state.
 *
 * `hoursFromHW` is hours relative to HW Victoria (clamped to −6…+6). `springRatio`
 * is 0 for neaps, 1 for springs; the rate is interpolated linearly between the
 * neap and spring columns, and set/rate are interpolated linearly between the
 * two bracketing hourly rows (set as an angle, the short way round).
 */
export function streamAt(diamond: TidalDiamond, hoursFromHW: number, springRatio: number): StreamSample {
  const h = Math.max(-6, Math.min(6, hoursFromHW));
  const sr = Math.max(0, Math.min(1, springRatio));
  const rows = diamond.hours; // index = h + 6
  const lo = Math.floor(h);
  const hi = Math.ceil(h);
  const f = h - lo;
  const rowLo = rows[lo + 6];
  const rowHi = rows[hi + 6];
  const rate = (row: DiamondHour) => row.np + (row.sp - row.np) * sr;
  const rateLo = rate(rowLo);
  const rateHi = rate(rowHi);
  return {
    setDeg: lerpAngleDeg(rowLo.dir, rowHi.dir, f),
    rateKn: rateLo + (rateHi - rateLo) * f,
  };
}

/**
 * Tidal stream at an arbitrary position and instant, taken from the NEAREST
 * chart diamond. Nearest-neighbour on purpose: the diamonds are sparse (a
 * handful across the training area), so this is an honest "use the closest
 * diamond" read, not a smooth interpolated field. Chart-diamond source only.
 * Used by the passage simulator to time a route against the tide.
 */
export function streamAtPosition(position: [number, number], ms: number): StreamSample {
  const { hoursFromHW, springRatio } = tidalStateAt(ms);
  let best: TidalDiamond | null = null;
  let bestD = Infinity;
  for (const d of TIDAL_DIAMONDS) {
    const dx = d.position[0] - position[0];
    const dy = d.position[1] - position[1];
    const dd = dx * dx + dy * dy;
    if (dd < bestD) {
      bestD = dd;
      best = d;
    }
  }
  if (!best) return { setDeg: 0, rateKn: 0 };
  return streamAt(best, hoursFromHW, springRatio);
}

export * from './victoria';
export * from './sources';
export * from './levels';
